import { mergeArrayById } from "@platform/hooks";

import type { ColumnContext } from "./valueGetters";
import type {
  CarrierColumn,
  CoverageMetadataEdit,
  FinancialsCoverageRef,
  PatchFinancialsTableRequest,
  PeriodKey,
  TotalsBlock,
  TotalsBlockEdit,
  TotalsBlockPeriodNotes,
} from "@platform/api";

/**
 * Parses a colId string (e.g., "period:current", "carrier:abc-123")
 * into a typed ColumnContext. Returns `null` for unrecognized formats
 * (label column, group column, etc.).
 */
export const parseColId = (colId: string): ColumnContext | null => {
  if (colId.startsWith("period:")) {
    return {
      kind: "period",
      period: colId.replace("period:", "") as PeriodKey,
    };
  }
  if (colId.startsWith("carrier:")) {
    return { kind: "carrier", carrierId: colId.replace("carrier:", "") };
  }

  return null;
};

const coverageById = (
  a: FinancialsCoverageRef,
  b: Partial<FinancialsCoverageRef>,
): boolean => a.id === b.id;

/**
 * The PATCH body for one coverage cell's edit. A carrier cell goes out as that
 * carrier alone, carrying only the edited cell: the route saves every premium
 * a body carries as an analyst override, so resending the other carriers'
 * cells would freeze each of them at its current value.
 */
const coverageCellDiff = (
  column: ColumnContext,
  coverageId: string,
  edit: { annualPremium: number | null } | { note: string | null },
): PatchFinancialsTableRequest =>
  column.kind === "period"
    ? { periods: { [column.period]: { coverages: { [coverageId]: edit } } } }
    : {
        carriers: [{ id: column.carrierId, coverages: { [coverageId]: edit } }],
      };

/**
 * Builds a sparse PATCH diff for an annual premium edit. `null` removes the
 * analyst's override, so the premium calculated from the Quotes rates returns.
 */
export const buildAnnualPremiumDiff = (
  column: ColumnContext,
  coverageId: string,
  value: number | null,
): PatchFinancialsTableRequest =>
  coverageCellDiff(column, coverageId, { annualPremium: value });

/**
 * Builds a sparse PATCH diff for a note edit.
 */
export const buildNoteDiff = (
  column: ColumnContext,
  coverageId: string,
  value: string | null,
): PatchFinancialsTableRequest =>
  coverageCellDiff(column, coverageId, { note: value });

const totalsBlockById = (a: TotalsBlock, b: Partial<TotalsBlock>): boolean =>
  a.id === (b as { id?: string }).id;

/**
 * Builds a PATCH diff for a totals block note edit.
 */
export const buildTotalsBlockNoteDiff = (
  column: ColumnContext,
  blockId: string,
  value: string | null,
  totalsBlocks: readonly TotalsBlock[],
): PatchFinancialsTableRequest => {
  const noteUpdate =
    column.kind === "period"
      ? { periodNotes: { [column.period]: value } as TotalsBlockPeriodNotes }
      : { carrierNotes: { [column.carrierId]: value } };

  return {
    totalsBlocks: mergeArrayById(
      totalsBlocks,
      [{ id: blockId, ...noteUpdate } as Partial<TotalsBlock>],
      totalsBlockById,
    ) as TotalsBlockEdit[],
  };
};

/**
 * Builds a PATCH diff for a credits row value edit.
 */
export const buildCreditsValueDiff = (
  column: ColumnContext,
  blockId: string,
  value: number | null,
  totalsBlocks: readonly TotalsBlock[],
): PatchFinancialsTableRequest => ({
  totalsBlocks: totalsBlocks.map((block) => {
    if (block.id !== blockId) return block;
    const creditsRow =
      column.kind === "period"
        ? {
            ...block.creditsRow,
            periodValues: {
              ...block.creditsRow.periodValues,
              [column.period]: value,
            },
          }
        : {
            ...block.creditsRow,
            carrierValues: {
              ...block.creditsRow.carrierValues,
              [column.carrierId]: value,
            },
          };
    return { ...block, creditsRow };
  }) as TotalsBlockEdit[],
});

/**
 * Builds a PATCH diff for toggling a credits row's visibility.
 */
export const buildToggleCreditsHiddenDiff = (
  blockId: string,
  currentlyHidden: boolean,
  totalsBlocks: readonly TotalsBlock[],
): PatchFinancialsTableRequest => ({
  totalsBlocks: totalsBlocks.map((block) =>
    block.id !== blockId
      ? block
      : {
          ...block,
          creditsRow: { ...block.creditsRow, hidden: !currentlyHidden },
        },
  ) as TotalsBlockEdit[],
});

/**
 * Builds a PATCH diff for editing a credits row label.
 */
export const buildCreditsLabelDiff = (
  blockId: string,
  label: string,
  totalsBlocks: readonly TotalsBlock[],
): PatchFinancialsTableRequest => ({
  totalsBlocks: totalsBlocks.map((block) =>
    block.id !== blockId
      ? block
      : { ...block, creditsRow: { ...block.creditsRow, label } },
  ) as TotalsBlockEdit[],
});

/**
 * Builds a PATCH diff for editing a totals block's Notes row header label.
 */
export const buildTotalsNotesLabelDiff = (
  blockId: string,
  label: string,
  totalsBlocks: readonly TotalsBlock[],
): PatchFinancialsTableRequest => ({
  totalsBlocks: totalsBlocks.map((block) =>
    block.id !== blockId ? block : { ...block, notesRowHeaderLabel: label },
  ) as TotalsBlockEdit[],
});

/**
 * Builds a sparse PATCH diff for toggling a period column's visibility.
 */
export const buildTogglePeriodHiddenDiff = (
  period: PeriodKey,
  currentlyHidden: boolean,
): PatchFinancialsTableRequest => ({
  periods: {
    [period]: { hidden: !currentlyHidden },
  },
});

/**
 * Builds a PATCH diff for toggling a carrier column's visibility. Every carrier
 * goes out, since the route reads the full hidden set from the list, but with
 * its visibility alone — no cells, which the route would save as overrides.
 */
export const buildToggleCarrierHiddenDiff = (
  carrierId: string,
  currentlyHidden: boolean,
  carriers: readonly CarrierColumn[],
): PatchFinancialsTableRequest => ({
  carriers: carriers.map((carrier) => ({
    id: carrier.id,
    hidden: carrier.id === carrierId ? !currentlyHidden : carrier.hidden,
  })),
});

/**
 * Builds a sparse PATCH diff for editing a coverage's Notes row header label.
 */
export const buildNotesLabelDiff = (
  coverageId: string,
  label: string,
  coverages: readonly FinancialsCoverageRef[],
): PatchFinancialsTableRequest => ({
  coverages: mergeArrayById(
    coverages,
    [{ id: coverageId, notesRowHeaderLabel: label }],
    coverageById,
  ) as CoverageMetadataEdit[],
});

/**
 * Builds a sparse PATCH diff for toggling a coverage's visibility.
 */
export const buildToggleCoverageHiddenDiff = (
  coverageId: string,
  currentlyHidden: boolean,
  coverages: readonly FinancialsCoverageRef[],
): PatchFinancialsTableRequest => ({
  coverages: mergeArrayById(
    coverages,
    [{ id: coverageId, hidden: !currentlyHidden }],
    coverageById,
  ) as CoverageMetadataEdit[],
});
